import React, { useState, useMemo, useCallback } from 'react';
import { Pencil, Check, X, ChevronDown, TrendingUp, TrendingDown, DollarSign, CalendarDays, Filter } from 'lucide-react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine
} from 'recharts';
import { useAppState } from '../store/AppContext';
import { Card } from './ui/Card';
import { formatVND } from '../utils/format';
import { Money, SensitiveValue } from './ui/SensitiveValue';
import { buildCashFlowProjection, MonthlyProjection, getTotalMonthlyDebtPayment, getAssetAnnualIncome } from '../utils/calculations';
import { Asset } from '../types';

// ── Time Range ────────────────────────────────────────────────────────────────
type RangeKey = 'this-year' | 'next-year' | 'rolling-12' | 'rolling-24';

function getRangeParams(key: RangeKey): { startMonth: number; startYear: number; months: number } {
  const now = new Date();
  const m = now.getMonth() + 1;
  const y = now.getFullYear();
  switch (key) {
    case 'this-year':   return { startMonth: 1, startYear: y, months: 12 };
    case 'next-year':   return { startMonth: 1, startYear: y + 1, months: 12 };
    case 'rolling-12':  return { startMonth: m, startYear: y, months: 12 };
    case 'rolling-24':  return { startMonth: m, startYear: y, months: 24 };
  }
}

const RANGE_OPTIONS: { value: RangeKey; label: string }[] = [
  { value: 'this-year',  label: `Năm ${new Date().getFullYear()}` },
  { value: 'next-year',  label: `Năm ${new Date().getFullYear() + 1}` },
  { value: 'rolling-12', label: '12 tháng tới' },
  { value: 'rolling-24', label: '24 tháng tới' },
];

// ── Custom Tooltip ────────────────────────────────────────────────────────────
function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const data: MonthlyProjection = payload[0]?.payload;
  if (!data) return null;

  return (
    <div style={{
      background: 'var(--tooltip-bg)', border: '1px solid var(--tooltip-bd)',
      borderRadius: 12, padding: '14px 16px', minWidth: 260,
      boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--tx-1)', marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        {data.label}
      </div>

      {/* Income breakdown */}
      {data.totalIncome > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>Thu nhập</div>
          {data.incomeItems.map((item, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 3 }}>
              <span style={{ fontSize: 11, color: 'var(--tx-4)' }}>{item.assetCode} · {item.label}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#10b981' }}><Money value={item.amount} /></span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 5, marginTop: 3, borderTop: '1px solid var(--bd)' }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx-3)' }}>Tổng thu</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#10b981' }}><Money value={data.totalIncome} /></span>
          </div>
        </div>
      )}

      {/* Expenses */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 10, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>Chi phí</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 3 }}>
          <span style={{ fontSize: 11, color: 'var(--tx-4)' }}>
            Chi phí sinh hoạt {data.actualExpense !== undefined ? '(thực tế)' : '(kế hoạch)'}
          </span>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#f87171' }}>
            <Money value={data.actualExpense !== undefined ? data.actualExpense : data.plannedExpense} />
          </span>
        </div>
        {data.debtItems.map((d, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 3 }}>
            <span style={{ fontSize: 11, color: 'var(--tx-4)' }}>Trả nợ · {d.name}</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#f87171' }}><Money value={d.amount} /></span>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 5, marginTop: 3, borderTop: '1px solid var(--bd)' }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx-3)' }}>Tổng chi</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#f87171' }}><Money value={data.totalExpense} /></span>
        </div>
      </div>

      {/* Maturity */}
      {data.totalMaturity > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>Đáo hạn</div>
          {data.maturityItems.map((m, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
              <span style={{ fontSize: 11, color: 'var(--tx-4)' }}>{m.label}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#fbbf24' }}><Money value={m.amount} /></span>
            </div>
          ))}
        </div>
      )}

      {/* Net */}
      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid var(--bd)' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-3)' }}>Dòng tiền ròng</span>
        <span style={{ fontSize: 14, fontWeight: 800, color: data.net >= 0 ? '#10b981' : '#f87171' }}>
          <Money value={data.net} prefix={data.net >= 0 ? '+' : ''} />
        </span>
      </div>
    </div>
  );
}

// ── Inline expense editor ─────────────────────────────────────────────────────
function ExpenseCell({ row, onSave }: { row: MonthlyProjection; onSave: (year: number, month: number, amount: number, notes: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');

  const startEdit = () => { setVal(String(row.actualExpense !== undefined ? row.actualExpense : row.plannedExpense)); setEditing(true); };
  const confirm = () => { onSave(row.year, row.month, parseFloat(val) || 0, ''); setEditing(false); };
  const cancel = () => setEditing(false);

  if (editing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input autoFocus value={val} onChange={e => setVal(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') confirm(); if (e.key === 'Escape') cancel(); }}
          style={{ width: 110, padding: '4px 8px', borderRadius: 6, fontSize: 12, outline: 'none', background: 'var(--bg-input)', border: '1px solid rgba(16,185,129,0.4)', color: 'var(--tx-1)' }} />
        <button onClick={confirm} style={{ width: 24, height: 24, borderRadius: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(16,185,129,0.15)', border: 'none', cursor: 'pointer', color: '#10b981' }}><Check size={11} /></button>
        <button onClick={cancel} style={{ width: 24, height: 24, borderRadius: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-card-el)', border: 'none', cursor: 'pointer', color: 'var(--tx-4)' }}><X size={11} /></button>
      </div>
    );
  }

  const isActual = row.actualExpense !== undefined;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
      <span style={{ fontSize: 13, fontWeight: 500, color: isActual ? 'var(--tx-1)' : 'var(--tx-3)' }}>
        <Money value={isActual ? row.actualExpense! : row.plannedExpense} />
      </span>
      {isActual && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: 'rgba(56,189,248,0.1)', color: '#38bdf8' }}>thực</span>}
      <button onClick={startEdit} style={{ width: 22, height: 22, borderRadius: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-card-el)', border: 'none', cursor: 'pointer', color: 'var(--tx-4)' }}
        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--tx-3)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--tx-4)'; }}>
        <Pencil size={10} />
      </button>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export function CashFlowProjection() {
  const { state, dispatch } = useAppState();
  const { assets, liabilities = [], monthlyExpenses = [], buckets } = state;

  const [rangeKey, setRangeKey] = useState<RangeKey>('this-year');
  const [plannedExpense, setPlannedExpense] = useState(50_000_000);
  const [selectedBuckets, setSelectedBuckets] = useState<string[]>([]);

  const toggleBucket = (id: string) =>
    setSelectedBuckets(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const rangeParams = getRangeParams(rangeKey);

  const projection = useMemo(() =>
    buildCashFlowProjection(assets, liabilities, {
      ...rangeParams,
      bucketFilter: selectedBuckets.length > 0 ? selectedBuckets : undefined,
      plannedExpense,
      monthlyExpenses,
    }), [assets, liabilities, rangeParams, selectedBuckets, plannedExpense, monthlyExpenses]);

  const handleSaveExpense = useCallback((year: number, month: number, amount: number, notes: string) => {
    if (amount <= 0) dispatch({ type: 'DELETE_MONTHLY_EXPENSE', payload: { year, month } });
    else dispatch({ type: 'SET_MONTHLY_EXPENSE', payload: { year, month, amount, notes } });
  }, [dispatch]);

  const totalIncome   = projection.reduce((s, m) => s + m.totalIncome, 0);
  const totalExpense  = projection.reduce((s, m) => s + m.totalExpense, 0);
  const totalNet      = projection.reduce((s, m) => s + m.net, 0);
  const totalMaturity = projection.reduce((s, m) => s + m.totalMaturity, 0);
  const coverage      = totalExpense > 0 ? totalIncome / totalExpense : 0;
  const monthlyDebt   = getTotalMonthlyDebtPayment(liabilities);

  const chartData = projection.map(m => ({
    ...m,
    expenseOnly: m.totalExpense - m.totalDebtPayment,
    debtPayment: m.totalDebtPayment,
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* ── Controls ── */}
      <div className="rsp-controls">
        {/* Range selector */}
        <div style={{ display: 'flex', gap: 4, padding: '4px', borderRadius: 10, background: 'var(--bg-card)', border: '1px solid var(--bd)' }}>
          {RANGE_OPTIONS.map(o => (
            <button key={o.value} onClick={() => setRangeKey(o.value)}
              style={{ padding: '6px 14px', borderRadius: 7, fontSize: 12, cursor: 'pointer', fontWeight: 500, border: 'none', transition: 'all 0.12s',
                background: rangeKey === o.value ? 'rgba(16,185,129,0.15)' : 'transparent',
                color: rangeKey === o.value ? '#10b981' : 'var(--tx-3)' }}>
              {o.label}
            </button>
          ))}
        </div>

        {/* Fund filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Filter size={12} color="var(--tx-4)" />
          {buckets.map(b => {
            const sel = selectedBuckets.includes(b.id);
            return (
              <button key={b.id} onClick={() => toggleBucket(b.id)}
                style={{ padding: '5px 11px', borderRadius: 7, fontSize: 11, cursor: 'pointer', fontWeight: 500, border: `1px solid ${sel ? `${b.color}35` : 'var(--bd)'}`, background: sel ? `${b.color}15` : 'transparent', color: sel ? b.color : 'var(--tx-4)', transition: 'all 0.12s' }}>
                {b.name}
              </button>
            );
          })}
          {selectedBuckets.length > 0 && (
            <button onClick={() => setSelectedBuckets([])} style={{ fontSize: 11, color: '#f87171', background: 'none', border: 'none', cursor: 'pointer' }}>Bỏ lọc</button>
          )}
        </div>

        {/* Planned expense */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
          <span style={{ fontSize: 11, color: 'var(--tx-4)', whiteSpace: 'nowrap' }}>Chi phí sống / tháng</span>
          <input type="number" value={plannedExpense} onChange={e => setPlannedExpense(parseFloat(e.target.value) || 0)}
            style={{ width: 'clamp(90px, 20vw, 130px)', padding: '6px 10px', borderRadius: 8, fontSize: 12, outline: 'none', background: 'var(--bg-input)', border: '1px solid var(--bd)', color: 'var(--tx-1)' }} />
        </div>
      </div>

      {/* ── KPI Row ── */}
      <div className="rsp-grid-kpi-5">
        {[
          { label: 'Tổng Thu nhập', value: <Money value={totalIncome} />, color: '#10b981', bg: 'rgba(16,185,129,0.06)', icon: <TrendingUp size={14} /> },
          { label: 'Tổng Chi phí', value: <Money value={totalExpense} />, color: '#f87171', bg: 'rgba(239,68,68,0.06)', icon: <TrendingDown size={14} /> },
          { label: 'Trả nợ / tháng', value: <Money value={monthlyDebt} />, color: '#fbbf24', bg: 'rgba(251,191,36,0.06)', icon: <DollarSign size={14} /> },
          { label: 'Đáo hạn kỳ này', value: <Money value={totalMaturity} />, color: '#38bdf8', bg: 'rgba(56,189,248,0.06)', icon: <CalendarDays size={14} /> },
          {
            label: 'Độ phủ dòng tiền',
            value: `${(coverage * 100).toFixed(0)}%`,
            color: coverage >= 1.2 ? '#10b981' : coverage >= 1 ? '#fbbf24' : '#f87171',
            bg: coverage >= 1 ? 'rgba(16,185,129,0.06)' : 'rgba(239,68,68,0.06)',
            sub: coverage >= 1.2 ? 'Dư dả ✓' : coverage >= 1 ? 'Vừa đủ' : 'Thiếu hụt',
            icon: null,
          },
        ].map((s, i) => (
          <div key={i} style={{ padding: '14px 16px', borderRadius: 13, background: s.bg, border: `1px solid ${s.color}18` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 10, color: 'var(--tx-3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500 }}>{s.label}</span>
              {s.icon && <span style={{ color: s.color }}>{s.icon}</span>}
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: s.color, letterSpacing: '-0.02em' }}>{s.value}</div>
            {(s as any).sub && <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 3 }}>{(s as any).sub}</div>}
          </div>
        ))}
      </div>

      {/* ── Chart ── */}
      <Card>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--bd)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-1)' }}>Biểu đồ Dòng tiền</div>
            <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 2 }}>
              {RANGE_OPTIONS.find(o => o.value === rangeKey)?.label}
              {selectedBuckets.length > 0 && ` · Lọc theo ${selectedBuckets.length} quỹ`}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            {[
              { color: '#10b981', label: 'Thu nhập' },
              { color: '#f87171', label: 'Chi phí sống' },
              { color: '#dc2626', label: 'Trả nợ' },
              { color: '#fbbf24', label: 'Đáo hạn' },
              { color: '#a78bfa', label: 'Ròng' },
            ].map(x => (
              <div key={x.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: x.color }} />
                <span style={{ fontSize: 11, color: 'var(--tx-3)' }}>{x.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ padding: '12px 6px 16px' }}>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={chartData} margin={{ top: 5, right: 16, left: 4, bottom: 5 }} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--bd)" vertical={false} />
              <XAxis dataKey="labelShort" tick={{ fill: 'var(--chart-tick)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={v => formatVND(v)} tick={{ fill: 'var(--chart-tick)', fontSize: 10 }} axisLine={false} tickLine={false} width={72} />
              <ReferenceLine y={0} stroke="rgba(255,255,255,0.08)" />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="totalIncome"  name="Thu nhập"   fill="#10b981" radius={[3,3,0,0]} opacity={0.85} maxBarSize={28} />
              <Bar dataKey="expenseOnly"  name="Chi phí sống" fill="#f87171" radius={[3,3,0,0]} opacity={0.7} maxBarSize={28} stackId="exp" />
              <Bar dataKey="debtPayment"  name="Trả nợ"     fill="#dc2626" radius={[0,0,0,0]} opacity={0.75} maxBarSize={28} stackId="exp" />
              <Bar dataKey="totalMaturity" name="Đáo hạn"   fill="#fbbf24" radius={[3,3,0,0]} opacity={0.8} maxBarSize={28} />
              <Line type="monotone" dataKey="net" name="Ròng" stroke="#a78bfa" strokeWidth={2.5} dot={false}
                activeDot={{ r: 4, fill: '#a78bfa', stroke: '#080f1e', strokeWidth: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* ── Monthly Detail Table ── */}
      <Card>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--bd)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-1)' }}>Chi tiết từng tháng</div>
          <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 2 }}>Click biểu tượng bút để ghi nhận chi phí thực tế</div>
        </div>
        <div className="table-wrap">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--bd)' }}>
                {[
                  { label: 'Tháng', align: 'left' as const },
                  { label: 'Thu nhập', align: 'right' as const },
                  { label: 'Chi phí sống', align: 'right' as const },
                  { label: 'Trả nợ', align: 'right' as const },
                  { label: 'Đáo hạn', align: 'right' as const },
                  { label: 'Ròng', align: 'right' as const },
                  { label: 'Tích lũy', align: 'right' as const },
                ].map(h => (
                  <th key={h.label} style={{ padding: '9px 16px', textAlign: h.align, fontSize: 11, fontWeight: 500, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                    {h.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {projection.map((m, i) => {
                const pos = m.net >= 0;
                const hasMat = m.totalMaturity > 0;
                const isCurrentMonth = m.year === new Date().getFullYear() && m.month === new Date().getMonth() + 1;
                return (
                  <tr key={i}
                    style={{ borderBottom: '1px solid var(--bd-sub)', transition: 'background 0.1s', background: isCurrentMonth ? 'rgba(16,185,129,0.04)' : 'transparent' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.background = 'var(--bg-row-hover)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = isCurrentMonth ? 'rgba(16,185,129,0.04)' : 'transparent'; }}>
                    <td style={{ padding: '11px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: isCurrentMonth ? 700 : 500, color: isCurrentMonth ? '#10b981' : 'var(--tx-1)' }}>{m.label}</span>
                        {isCurrentMonth && <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 3, background: 'rgba(16,185,129,0.15)', color: '#10b981' }}>Hiện tại</span>}
                        {hasMat && <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 3, background: 'rgba(251,191,36,0.15)', color: '#fbbf24' }}>Đáo hạn</span>}
                      </div>
                    </td>
                    <td style={{ padding: '11px 16px', textAlign: 'right' }}>
                      {m.totalIncome > 0 ? (
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#10b981' }}><Money value={m.totalIncome} /></div>
                          <div style={{ fontSize: 10, color: '#10b981', marginTop: 1 }}>{m.incomeItems.length} nguồn</div>
                        </div>
                      ) : <span style={{ color: 'var(--tx-5)', fontSize: 12 }}>—</span>}
                    </td>
                    <td style={{ padding: '11px 16px' }}>
                      <ExpenseCell row={m} onSave={handleSaveExpense} />
                    </td>
                    <td style={{ padding: '11px 16px', textAlign: 'right' }}>
                      {m.totalDebtPayment > 0
                        ? <span style={{ fontSize: 13, fontWeight: 500, color: '#dc2626' }}><Money value={m.totalDebtPayment} /></span>
                        : <span style={{ color: 'var(--tx-5)', fontSize: 12 }}>—</span>}
                    </td>
                    <td style={{ padding: '11px 16px', textAlign: 'right' }}>
                      {hasMat
                        ? <span style={{ fontSize: 13, fontWeight: 600, color: '#fbbf24' }}><Money value={m.totalMaturity} /></span>
                        : <span style={{ color: 'var(--tx-5)', fontSize: 12 }}>—</span>}
                    </td>
                    <td style={{ padding: '11px 16px', textAlign: 'right' }}>
                      <span style={{ fontSize: 13, fontWeight: 700, padding: '3px 9px', borderRadius: 6, background: pos ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)', color: pos ? '#10b981' : '#f87171' }}>
                        <Money value={m.net} prefix={pos ? '+' : ''} />
                      </span>
                    </td>
                    <td style={{ padding: '11px 16px', textAlign: 'right' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: m.cumulativeNet >= 0 ? 'var(--tx-4)' : '#f87171' }}>
                        <Money value={m.cumulativeNet} prefix={m.cumulativeNet >= 0 ? '+' : ''} />
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '1px solid var(--bd)', background: 'var(--bg-card-el)' }}>
                <td style={{ padding: '12px 16px', fontSize: 12, fontWeight: 700, color: 'var(--tx-3)' }}>Tổng cộng</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 13, fontWeight: 700, color: '#10b981' }}><Money value={totalIncome} /></td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 13, fontWeight: 700, color: '#f87171' }}><Money value={projection.reduce((s, m) => s + (m.actualExpense ?? m.plannedExpense), 0)} /></td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 13, fontWeight: 700, color: '#dc2626' }}><Money value={projection.reduce((s, m) => s + m.totalDebtPayment, 0)} /></td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 13, fontWeight: 700, color: '#fbbf24' }}><Money value={totalMaturity} /></td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, fontWeight: 800, color: totalNet >= 0 ? '#10b981' : '#f87171' }}><Money value={totalNet} prefix={totalNet >= 0 ? '+' : ''} /></td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      {/* ── Income Source Summary ── */}
      <IncomeSourceSummary assets={state.assets} selectedBuckets={selectedBuckets} />
    </div>
  );
}

// ── Income Source Summary ─────────────────────────────────────────────────────

function IncomeSourceSummary({ assets, selectedBuckets }: { assets: Asset[]; selectedBuckets: string[] }) {
  const [open, setOpen] = useState(false);
  const relevantAssets = selectedBuckets.length > 0
    ? assets.filter(a => selectedBuckets.includes(a.bucketId))
    : assets;
  const incomeAssets = relevantAssets.filter(a => getAssetAnnualIncome(a) > 0);
  const total = incomeAssets.reduce((s, a) => s + getAssetAnnualIncome(a), 0);

  return (
    <Card>
      <button onClick={() => setOpen(v => !v)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', background: 'none', border: 'none', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-1)' }}>Nguồn thu nhập</span>
          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 5, background: 'rgba(16,185,129,0.1)', color: '#10b981' }}>
            {incomeAssets.length} nguồn · <Money value={total} />/năm
          </span>
        </div>
        <ChevronDown size={14} color="var(--tx-4)" style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
      </button>
      {open && (
        <div style={{ borderTop: '1px solid var(--bd)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--bd)' }}>
                {['Mã / Tài sản', 'Lịch thanh toán', 'Thu nhập / năm', 'Yield'].map((h, i) => (
                  <th key={h} style={{ padding: '9px 16px', fontSize: 11, fontWeight: 500, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: i > 1 ? 'right' as const : 'left' as const }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {incomeAssets.map((a, i) => {
                const annual = getAssetAnnualIncome(a);
                const events = a.incomeEvents || [];
                const costBasis = a.costPrice * a.quantity;
                const yieldPct = costBasis > 0 ? (annual / costBasis) * 100 : 0;
                return (
                  <tr key={a.id} style={{ borderBottom: i < incomeAssets.length - 1 ? '1px solid var(--bd-sub)' : 'none' }}>
                    <td style={{ padding: '11px 16px' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-1)' }}>{a.name}</div>
                    </td>
                    <td style={{ padding: '11px 16px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {events.slice(0, 6).map(ev => (
                          <span key={ev.id} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: 'rgba(16,185,129,0.08)', color: '#10b981', whiteSpace: 'nowrap' }}>
                            T{ev.month}: <Money value={ev.amount} />
                          </span>
                        ))}
                        {events.length > 6 && <span style={{ fontSize: 10, color: 'var(--tx-4)' }}>+{events.length - 6} tháng</span>}
                        {events.length === 0 && <span style={{ fontSize: 11, color: 'var(--tx-4)' }}>Chưa cấu hình</span>}
                      </div>
                    </td>
                    <td style={{ padding: '11px 16px', textAlign: 'right', fontSize: 13, fontWeight: 700, color: '#10b981' }}><Money value={annual} /></td>
                    <td style={{ padding: '11px 16px', textAlign: 'right' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#fbbf24' }}>{yieldPct.toFixed(1)}%</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
